import { type Entry, entryKeys } from '@taler/api-client'
import { useInfiniteQuery } from '@tanstack/vue-query'
import { type CurrencyCode, formatLocalDate } from '@taler/domain'
import { computed, type ComputedRef } from 'vue'
import { useApi } from '@/app/api'
import { keepPreviousListResult, sameLedgerAndKeyHolds } from '@/app/keep-previous-list'
import { retrying } from '@/app/retrying'
import type { PeriodRange } from '../internal/flow-period.ts'

export const ACCOUNT_ENTRIES_PAGE_SIZE = 25

export interface UseAccountEntriesOptions {
  readonly ledgerId: () => string | undefined
  readonly accountId: () => string
  readonly includeDescendants: () => boolean
  readonly range: () => PeriodRange
  readonly displayCurrency: () => CurrencyCode
  readonly enabled: () => boolean
}

export interface UseAccountEntries {
  readonly entries: ComputedRef<readonly Entry[]>
  readonly isLoading: ComputedRef<boolean>
  readonly isFetchingNextPage: ComputedRef<boolean>
  readonly hasNextPage: ComputedRef<boolean>
  readonly error: ComputedRef<Error | null>
  readonly retrying: ComputedRef<boolean>
  fetchNextPage(): void
  retry(): void
}

export function useAccountEntries(options: UseAccountEntriesOptions): UseAccountEntries {
  const api = useApi()
  const active = computed(() => options.enabled() && options.ledgerId() !== undefined)

  const query = () => {
    const range = options.range()
    return {
      limit: ACCOUNT_ENTRIES_PAGE_SIZE,
      accountId: options.accountId(),
      includeDescendants: options.includeDescendants(),
      displayCurrency: options.displayCurrency(),
      ...(range.from === undefined ? {} : { from: formatLocalDate(range.from) }),
      until: formatLocalDate(range.until),
    }
  }

  const entriesQuery = useInfiniteQuery({
    queryKey: computed(() => [...entryKeys.list(options.ledgerId() ?? '', query()), options.accountId()]),
    placeholderData: computed(() => {
      const ledgerId = options.ledgerId()
      const accountId = options.accountId()
      return keepPreviousListResult(sameLedgerAndKeyHolds(ledgerId, accountId))
    }),
    queryFn: ({ pageParam, signal }) =>
      api.entries.list(
        options.ledgerId()!,
        { ...query(), ...(pageParam === undefined ? {} : { cursor: pageParam as string }) },
        signal,
      ),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.nextCursor,
    enabled: active,
    retry: false,
  })

  return {
    entries: computed(() => (entriesQuery.data.value?.pages ?? []).flatMap((page) => page.items)),
    isLoading: computed(() => active.value && entriesQuery.isPending.value),
    isFetchingNextPage: computed(() => entriesQuery.isFetchingNextPage.value),
    hasNextPage: computed(() => entriesQuery.hasNextPage.value),
    error: computed(() => entriesQuery.error.value),
    retrying: retrying(entriesQuery),
    fetchNextPage: () => {
      void entriesQuery.fetchNextPage()
    },
    retry: () => {
      void entriesQuery.refetch()
    },
  }
}
